import {
  AdvancedSearchConfig,
  isSubmittableSortField,
  SortField,
} from "@/config/forms/advanced-search/advanced-search-fields-def";
import { useAdvancedSearch } from "@/context/advanced-search/AdvancedSearchContext";
import { PopoverClose } from "@radix-ui/react-popover";
import { ChevronsUpDownIcon } from "lucide-react";
import { forwardRef, useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { Form, FormField } from "../ui/form";
import { FormCol } from "../ui/form-col";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { SortingButton } from "../ui/sorting-button";
import { AdvancedQueryFieldSelector } from "./AdvancedQueryFieldSelector";
import { Sortable, SortableDragHandle, SortableItem } from "../ui/sortable";
import { DragHandleDots2Icon } from "@radix-ui/react-icons";
import { DeleteButton } from "../ui/custom-buttons";

const AdvancedSortFormRow = forwardRef<
  HTMLDivElement,
  {
    rowIndex: number;
    config: AdvancedSearchConfig;
    onDeleteRow: () => void;
  }
>(({ config, rowIndex, onDeleteRow }, ref) => {
  return (
    <FormField
      name={`fields.${rowIndex}`}
      rules={{ validate: (v) => v.sortMode && v.key && v.key.length > 0 }}
      render={({ field }) => (
        <div ref={ref} className="flex items-center gap-3 w-full">
          <SortableDragHandle
            variant="outline"
            size="icon"
            className="size-8 shrink-0"
          >
            <DragHandleDots2Icon className="size-4" aria-hidden="true" />
          </SortableDragHandle>

          <AdvancedQueryFieldSelector
            className="w-full"
            allowForeign={false}
            config={config}
            value={field.value}
            onSelect={(value) => {
              field.onChange({
                ...field.value,
                key: value.key,
              });
            }}
          />
          <SortingButton
            sort={field.value.sortMode}
            onClick={(direction) => {
              field.onChange({
                ...field.value,
                sortMode: direction,
              });
            }}
          />
          <DeleteButton onClick={onDeleteRow} />
        </div>
      )}
    />
  );
});

export const AdvancedSort = ({ config }: { config: AdvancedSearchConfig }) => {
  const { t } = useTranslation();

  const { sortFields, setSortFields, resetSortFields } = useAdvancedSearch();

  const form = useForm<{ fields: SortField[] }>({
    defaultValues: {
      fields: [{ key: undefined, sortMode: false }],
    },
  });

  const { reset, control } = form;
  const rows = useFieldArray({
    name: "fields",
    control,
  });

  useEffect(() => {
    reset({ fields: sortFields ?? [{ key: undefined, sortMode: false }] });
  }, [reset, sortFields]);

  function onResetSort() {
    resetSortFields();
    reset({ fields: [{ key: undefined, sortMode: false }] });
  }

  function onSubmit(data: { fields: SortField[] }) {
    const submittable = data.fields.filter(isSubmittableSortField);

    if (submittable.length == 0) {
      resetSortFields();
      return;
    }

    setSortFields(data.fields.filter(isSubmittableSortField));
  }

  function onAddRow() {
    rows.append({ key: undefined, sortMode: false });
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="flex gap-2 px-3 group">
          {
            <ChevronsUpDownIcon className="group-hover:stroke-white stroke-gray-500" />
          }
          {t("dashBoardSortResults")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[500px] p-0">
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          onReset={onResetSort}
          className="flex flex-col "
        >
          <Form {...form}>
            <FormCol className="p-4 items-start">
              <h2 className="text-left">{t("dashBoardSortResults")}</h2>
              <Sortable
                value={rows.fields}
                onMove={({ activeIndex, overIndex }) =>
                  rows.move(activeIndex, overIndex)
                }
              >
                {rows.fields.map((field, index) => (
                  <SortableItem
                    key={field.id}
                    value={field.id}
                    className="w-full"
                  >
                    <AdvancedSortFormRow
                      rowIndex={index}
                      config={config}
                      onDeleteRow={() => rows.remove(index)}
                    />
                  </SortableItem>
                ))}
              </Sortable>

              <Button
                disabled={!form.formState.isValid}
                type="button"
                variant={"link"}
                className="font-semibold"
                onClick={onAddRow}
              >
                {t("advancedSortAddSort")}
              </Button>
            </FormCol>

            <div className="flex justify-between w-full h-fit p-4 relative bottom-0 border-t border-gray-200 shadow-[#1018280F_0px_2px_4px_-2px]">
              <PopoverClose asChild>
                <Button
                  type="button"
                  className="font-semibold"
                  onClick={onResetSort}
                  variant={"link"}
                >
                  {t("buttonReset")}
                </Button>
              </PopoverClose>
              <PopoverClose asChild>
                <Button
                  disabled={!form.formState.isValid}
                  type="submit"
                  variant={"default"}
                >
                  {t("advancedSortSort")}
                </Button>
              </PopoverClose>
            </div>
          </Form>
        </form>
      </PopoverContent>
    </Popover>
  );
};
